import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Paper,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  keyframes
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAuth } from './AuthContext';
import { API_BASE } from './config';

const TAOBAO_PURCHASE_URL = 'https://m.tb.cn/h.isD0Lm0?tk=R7AQ58Ofe1V';

// 通道卡片光晕呼吸动画
const channelGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 1px rgba(255,255,255,0.08), 0 20px 60px -20px rgba(99,102,241,0.45); }
  50% { box-shadow: 0 0 0 1px rgba(255,255,255,0.12), 0 30px 80px -20px rgba(236,72,153,0.55); }
`;

// 通道卡片背景渐变流动
const channelShine = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// 1. 太阳自转动画
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// 2. 星星闪烁动画
const twinkle = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
`;

// 3. 彩虹生长动画
const rainbowDraw = keyframes`
  from { stroke-dashoffset: 300; }
  to { stroke-dashoffset: 0; }
`;

// 4. 星星从小变大闪烁动画
const growFade = keyframes`
  0% { transform: scale(0); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
`;

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [payHint, setPayHint] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [buyingPlan, setBuyingPlan] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyTaobaoLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(TAOBAO_PURCHASE_URL);
      } else {
        const ta = document.createElement('textarea');
        ta.value = TAOBAO_PURCHASE_URL;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setPayHint('✅ 已复制购买链接');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setPayHint('❌ 复制失败，请手动长按链接');
    }
  };

  const refreshUserRef = React.useRef(refreshUser);
  React.useEffect(() => {
    refreshUserRef.current = refreshUser;
  }, [refreshUser]);

  React.useEffect(() => {
    if (!payHint) return;
    const timer = window.setTimeout(() => setPayHint(null), 3500);
    return () => window.clearTimeout(timer);
  }, [payHint]);

  // 处理支付回调
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('order_id');

    if (orderId && user) {
      // 查询订单状态
      const token = localStorage.getItem('access_token');
      if (token) {
        fetch(`${API_BASE}/api/pay/query/${orderId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then(res => res.json())
          .then(data => {
            if (data.status === 'paid') {
              setPayHint('✅ 支付成功！套餐已生效');
              // 刷新用户信息
              refreshUserRef.current?.();
              // 清除URL参数
              window.history.replaceState({}, '', '/pricing');
            } else if (data.status === 'pending') {
              setPayHint('⏳ 支付处理中，请稍候...');
            } else {
              setPayHint('❌ 支付失败，请重试');
            }
          })
          .catch(() => {
            setPayHint('查询订单状态失败');
          });
      }
    }
  }, [location.search, user]);

  // TODO: 接入新支付接口后，在此处添加订单回调处理逻辑
  // 示例：监听 URL 参数 order_id，查询订单状态并显示支付结果

  const skuFor = (title: string) => {
    if (title === 'Creator') return isYearly ? 'creator_quarter' : 'creator_month';
    if (title === 'Business') return isYearly ? 'business_quarter' : 'business_month';
    return null;
  };

  const handleBuy = async (title: string) => {
    if (buying) return;
    const sku = skuFor(title);
    if (!sku) return;

    if (!user) {
      setPayHint('请先登录后再购买套餐');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setPayHint('登录已过期，请重新登录');
      return;
    }

    setBuying(true);
    setBuyingPlan(title);
    setPayHint(null);

    try {
      const res = await fetch(`${API_BASE}/api/pay/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sku,
          pay_type: 'alipay' // 默认使用支付宝，可以让用户选择
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || '创建支付失败');
      }

      if (!data?.pay_url) {
        throw new Error('创建支付失败：未返回支付链接');
      }

      // 跳转到支付页面
      window.location.href = data.pay_url;

    } catch (e: any) {
      setPayHint(e?.message || '创建支付失败');
      setBuying(false);
      setBuyingPlan(null);
    }
  };

  const handleBack = () => {
    // 直接返回上一页，如果有历史记录
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  // 根据用户当前角色动态生成套餐配置
  const getCurrentRole = () => {
    if (!user) return null;
    return user.role || 'free';
  };

  const currentRole = getCurrentRole();

  const plans = [
    {
      title: 'Free',
      price: 0,
      monthlyPrice: 0,
      quarterlyPrice: 0,
      monthlyPeriod: '月',
      quarterlyPeriod: '季',
      period: '月',
      description: '享有以下权益',
      features: [
        '享有主页全部模型的使用权限',
        '享有优选页面的大部分功能使用'
      ],
      // Free套餐永远是当前套餐状态
      buttonText: '当前套餐',
      buttonVariant: 'outlined' as const,
      highlight: false
    },
    {
      title: 'Creator',
      price: 16.5,
      monthlyPrice: 99.9,
      quarterlyPrice: 299,
      monthlyPeriod: '月',
      quarterlyPeriod: '季',
      originalPrice: 240,
      yearlyPrice: 198,
      save: 42,
      period: '月',
      description: 'Free套餐包含的所有内容，此外：',
      features: [
        '1024积分/月，按年过期',
        '享有优选页面全部功能使用',
        '所有接口积分费用都相比原套餐价便宜约5%（除积分消耗小于等于5积分的接口）'
      ],
      // 根据用户当前角色动态设置按钮状态
      buttonText: currentRole === 'creator' ? '当前套餐' : '获取套餐',
      buttonVariant: currentRole === 'creator' ? 'outlined' as const : 'contained' as const,
      highlight: false,
      crown: true
    },
    {
      title: 'Business',
      monthlyPrice: 280,
      quarterlyPrice: 836,
      monthlyPeriod: '月',
      quarterlyPeriod: '季',
      description: 'Free套餐包含的所有内容，此外：',
      features: [
        '3000积分/月，按年过期',
        '享有优选页面全部功能使用',
        '所有接口积分费用都相比原套餐价便宜约10%（除积分消耗小于等于5积分的接口）'
      ],
      // 根据用户当前角色动态设置按钮状态
      buttonText: currentRole === 'business' ? '当前套餐' : '获取套餐',
      buttonVariant: currentRole === 'business' ? 'outlined' as const : 'contained' as const,
      highlight: false
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #dbeafe 0%, #ffffff 100%)',
      color: '#1e293b',
      pb: 8
    }}>
      {/* 此页面不显示顶部导航栏 */}

      {payHint && (
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            left: 0,
            right: 0,
            zIndex: 30,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#1e293b',
              bgcolor: 'rgba(255,255,255,0.7)',
              px: 2,
              py: 1,
              borderRadius: 2
            }}
          >
            {payHint}
          </Typography>
        </Box>
      )}

      {/* 左上角返回主页箭头 */}
      <Box sx={{ position: 'fixed', top: 16, left: 16, zIndex: 10 }}>
        <IconButton
          aria-label="返回上一页"
          onClick={handleBack}
          sx={{
            bgcolor: 'transparent',
            color: '#1e293b',
            border: 'none',
            boxShadow: 'none',
            backdropFilter: 'none',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* 顶部动画区域 */}
        <Box sx={{ position: 'relative', height: '120px', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', px: { xs: 2, md: 10 } }}>

          {/* 左侧红框：太阳与星星 (银色带点黄) */}
          <Box sx={{ width: '200px', height: '100%', position: 'relative' }}>
             {/* 太阳 */}
             <Box sx={{ position: 'absolute', top: '20%', left: '20%', width: 60, height: 60 }}>
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                   <defs>
                      <radialGradient id="sunGrad" cx="50%" cy="50%" r="50%">
                         <stop offset="0%" stopColor="#ffffff" />
                         <stop offset="100%" stopColor="#fef08a" />
                      </radialGradient>
                   </defs>
                   {/* 核心 */}
                   <circle cx="50" cy="50" r="25" fill="url(#sunGrad)" filter="drop-shadow(0 0 5px rgba(255,255,255,0.8))" />
                   {/* 光芒外圈 - 自转 */}
                   <g style={{ animation: `${rotate} 10s linear infinite`, transformOrigin: '50px 50px' }}>
                      {[...Array(8)].map((_, i) => (
                         <path
                           key={i}
                           d="M50 10 L55 30 L50 35 L45 30 Z"
                           fill="#fef08a"
                           transform={`rotate(${i * 45} 50 50)`}
                           opacity="0.8"
                         />
                      ))}
                   </g>
                </svg>
             </Box>
             {/* 四角星星 - 闪烁 */}
             {[
              { top: '10%', left: '60%', size: 20, delay: '0s' },
              { top: '60%', left: '10%', size: 15, delay: '1s' },
              { top: '50%', left: '80%', size: 25, delay: '0.5s' }
             ].map((star, i) => (
               <Box key={i} sx={{ position: 'absolute', top: star.top, left: star.left, width: star.size, height: star.size, animation: `${twinkle} 2s ease-in-out infinite`, animationDelay: star.delay }}>
                 <svg viewBox="0 0 100 100" width="100%" height="100%">
                   <path d="M50 0 C55 40 60 45 100 50 C60 55 55 60 50 100 C45 60 40 55 0 50 C40 45 45 40 50 0 Z" fill="#ffffff" filter="drop-shadow(0 0 2px #fef08a)" />
                 </svg>
               </Box>
             ))}
          </Box>

          {/* 中间红框：彩虹 (半圈生长) */}
          <Box sx={{ width: '200px', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', pb: 1 }}>
             <svg viewBox="0 0 200 100" width="180" height="90" style={{ overflow: 'visible' }}>
                <defs>
                   <linearGradient id="rainbowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ff9a9e" />
                      <stop offset="25%" stopColor="#fad0c4" />
                      <stop offset="50%" stopColor="#a18cd1" />
                      <stop offset="75%" stopColor="#fbc2eb" />
                      <stop offset="100%" stopColor="#8fd3f4" />
                   </linearGradient>
                </defs>
                {/* 背景轨道 (可选) */}
                <path d="M10 100 A 90 90 0 0 1 190 100" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" strokeLinecap="round" />
                {/* 动态彩虹 */}
                <path
                  d="M10 100 A 90 90 0 0 1 190 100"
                  fill="none"
                  stroke="url(#rainbowGrad)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 300,
                    strokeDashoffset: 300,
                    animation: `${rainbowDraw} 2.5s ease-out forwards`
                  }}
                />
             </svg>
          </Box>

          {/* 右侧红框：星星从小变大 (重复) */}
          <Box sx={{ width: '200px', height: '100%', position: 'relative' }}>
             {[
               { top: '30%', left: '30%', size: 30, delay: '0s' },
               { top: '60%', left: '60%', size: 20, delay: '1.5s' },
               { top: '20%', left: '70%', size: 25, delay: '0.8s' },
               { top: '70%', left: '20%', size: 15, delay: '2.2s' }
             ].map((star, i) => (
                <Box key={i} sx={{ position: 'absolute', top: star.top, left: star.left, width: star.size, height: star.size, animation: `${growFade} 3s ease-in-out infinite`, animationDelay: star.delay, opacity: 0 }}>
                   <svg viewBox="0 0 100 100" width="100%" height="100%">
                      <path d="M50 0 C55 40 60 45 100 50 C60 55 55 60 50 100 C45 60 40 55 0 50 C40 45 45 40 50 0 Z" fill="#ffffff" filter="drop-shadow(0 0 3px #fef08a)" />
                   </svg>
                </Box>
             ))}
          </Box>

        </Box>

        {/* Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6, alignItems: 'center' }}>
          <Box
            sx={{
              bgcolor: '#1f2937',
              borderRadius: '100px',
              p: 0.5,
              display: 'flex',
              position: 'relative'
            }}
          >
            <Button
              onClick={() => setIsYearly(false)}
              sx={{
                borderRadius: '100px',
                px: 4,
                py: 1,
                color: !isYearly ? 'black' : 'gray',
                bgcolor: !isYearly ? 'white' : 'transparent',
                '&:hover': { bgcolor: !isYearly ? 'white' : 'rgba(255,255,255,0.1)' }
              }}
            >
              月付
            </Button>
            <Button
              onClick={() => setIsYearly(true)}
              sx={{
                borderRadius: '100px',
                px: 4,
                py: 1,
                color: isYearly ? 'black' : 'gray',
                bgcolor: isYearly ? 'white' : 'transparent',
                '&:hover': { bgcolor: isYearly ? 'white' : 'rgba(255,255,255,0.1)' }
              }}
            >
              季付
              <Chip
                label="值得长期购买"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: 'transparent',
                  color: '#6366f1',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: 'auto',
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            </Button>
          </Box>


        </Box>

        <Grid container spacing={3} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 3 }} key={index}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  bgcolor: '#111827',
                  color: 'white',
                  borderRadius: 4,
                  border: plan.highlight ? '2px solid #6366f1' : '1px solid #374151',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: plan.highlight ? '0 0 20px rgba(99, 102, 241, 0.3)' : 'none'
                }}
              >
                {plan.highlight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)'
                    }}
                  />
                )}

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {plan.crown && <span style={{ color: '#fbbf24' }}>👑</span>}
                  {plan.title}
                </Typography>

                <Box sx={{ mb: 4 }}>
                  {(() => {
                    const displayPrice = isYearly
                      ? (plan as any).quarterlyPrice ?? plan.price
                      : (plan as any).monthlyPrice ?? plan.price;
                    const displayPeriod = isYearly
                      ? (plan as any).quarterlyPeriod ?? plan.period
                      : (plan as any).monthlyPeriod ?? plan.period;
                    const unitLabel = displayPeriod;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>￥{displayPrice}</Typography>
                        <Typography variant="subtitle1" sx={{ color: 'gray', ml: 1 }}>CNY / {unitLabel}</Typography>
                      </Box>
                    );
                  })()}
                </Box>

           {/* Removed slider placeholder for Pro as requested */}

                <Button
                  variant={plan.buttonVariant}
                  fullWidth
                  disabled={plan.buttonText === '当前套餐' || (buying && buyingPlan === plan.title)}
                  onClick={() => handleBuy(plan.title)}
                  sx={{
                    mb: 4,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: plan.buttonVariant === 'contained' ? '#6366f1' : 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&.Mui-disabled': {
                      color: 'white',
                      opacity: 1,
                      bgcolor: plan.buttonVariant === 'contained' ? '#6366f1' : 'rgba(255,255,255,0.1)'
                    },
                    '&:hover': {
                      bgcolor: plan.buttonVariant === 'contained' ? '#4f46e5' : 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  {plan.buttonText}
                </Button>

                <Typography variant="body2" sx={{ mb: 2, color: 'gray' }}>
                  {plan.description}
                </Typography>

                <List dense sx={{ flexGrow: 1 }}>
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckIcon sx={{ color: 'gray', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2', color: 'gray' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {(plan.title === 'Creator' || plan.title === 'Business') && (
                  isYearly ? (
                    <Typography variant="caption" sx={{ color: 'gray', ml: 4, mt: 1 }}>
                      注：3个月为一季
                    </Typography>
                  ) : (
                    // 占位以保持与季付相同的卡片高度，避免切换顿搓感
                    <Typography variant="caption" sx={{ visibility: 'hidden', ml: 4, mt: 1 }}>
                      注：3个月为一季
                    </Typography>
                  )
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* 第三方购买通道（淘宝/闲鱼）— 宽度 = 单张套餐卡，左缘对齐 Free 卡下方 */}
        <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              p: 2.5,
              color: 'white',
              background: 'linear-gradient(120deg, #0f172a 0%, #1e1b4b 35%, #312e81 65%, #581c87 100%)',
              backgroundSize: '200% 200%',
              animation: `${channelShine} 14s ease-in-out infinite alternate, ${channelGlow} 5s ease-in-out infinite`,
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {/* 顶部渐变高光线 */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #6366f1, #a855f7, #ec4899, transparent)',
              }}
            />
            {/* 背景柔光 */}
            <Box
              sx={{
                position: 'absolute',
                top: -120,
                right: -80,
                width: 320,
                height: 320,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -100,
                left: -60,
                width: 280,
                height: 280,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(99,102,241,0.30) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            <Box sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 2,
            }}>
              {/* 左侧文案 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(6px)',
                  }}>
                    <StorefrontIcon sx={{ color: '#fbbf24', fontSize: 18 }} />
                  </Box>
                  <Chip
                    label="官方授权店铺"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(251,191,36,0.15)',
                      color: '#fde68a',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      height: 20,
                      border: '1px solid rgba(251,191,36,0.3)',
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    background: 'linear-gradient(90deg, #ffffff 0%, #e0e7ff 60%, #fce7f3 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.02em',
                  }}
                >
                  淘宝 / 闲鱼 购买通道
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, lineHeight: 1.6, fontSize: '0.82rem' }}>
                  通过店铺通道下单，享有与本页同等套餐权益。适合需要发票、对公支付或淘宝/闲鱼优惠券的用户。
                </Typography>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                  fontSize: '0.78rem',
                  color: 'rgba(255,255,255,0.85)',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}>
                  <Box component="span" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {TAOBAO_PURCHASE_URL}
                  </Box>
                </Box>
              </Box>

              {/* 按钮区（垂直堆叠） */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
              }}>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(TAOBAO_PURCHASE_URL, '_blank', 'noopener,noreferrer')}
                  sx={{
                    px: 2.5,
                    py: 1.1,
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRadius: 2,
                    color: 'white',
                    background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #6366f1 100%)',
                    boxShadow: '0 10px 30px -10px rgba(236,72,153,0.6)',
                    textTransform: 'none',
                    letterSpacing: '0.02em',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #db2777 0%, #9333ea 50%, #4f46e5 100%)',
                      boxShadow: '0 14px 36px -10px rgba(236,72,153,0.75)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  前往该通道购买
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyTaobaoLink}
                  sx={{
                    px: 2.5,
                    py: 1.1,
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    borderRadius: 2,
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.25)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(6px)',
                    textTransform: 'none',
                    letterSpacing: '0.02em',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.5)',
                      bgcolor: 'rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  {copied ? '已复制 ✓' : '复制购买链接'}
                </Button>
              </Box>
            </Box>
          </Paper>
          </Grid>
          {/* 占位格：让通道卡对齐 Free 卡所在的左侧第一列 */}
          <Grid size={{ md: 6, lg: 3 }} sx={{ display: { xs: 'none', md: 'block' } }} />
          <Grid size={{ lg: 3 }} sx={{ display: { xs: 'none', lg: 'block' } }} />
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingPage;